// Gaussian Blur — the first concrete filter and the reference implementation
// every other M4 filter will copy structurally.
//
// Implementation notes:
//
//   - Separable kernel. We blur horizontally then vertically with two 1D
//     passes. Cost is O(w * h * k), not O(w * h * k^2), where k is the
//     kernel length.
//
//   - Sigma from radius. Photoshop exposes a "Radius" slider in pixels. The
//     widely-used convention (and what matches Photoshop output to within
//     rounding) is sigma = radius / 3.0, with the kernel truncated at ±3σ.
//     Kernel length is then approximately 6σ + 1 rounded to the nearest odd
//     integer (`2 * ceil(3 * sigma) + 1`).
//
//   - Edge handling: clamp-to-edge. When the kernel hangs off the side of
//     the buffer, we repeat the edge pixel rather than wrapping or treating
//     out-of-range samples as transparent. This matches Photoshop's
//     Gaussian Blur and prevents dark/transparent halos at the image
//     borders.
//
//   - Premultiplied alpha. Convolving non-premultiplied RGBA causes a halo:
//     transparent neighbours have arbitrary RGB and bleed colour into
//     partially-opaque pixels. We blur in premultiplied space (R*A/255,
//     G*A/255, B*A/255, A) and unpremultiply at the end. This is the
//     classic and correct fix.
//
//   - Selection awareness: NOT handled here. `applyFilterInPlace` lerps the
//     output with the original using the selection mask. The filter itself
//     always operates on the whole buffer.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface GaussianBlurParams {
  /** Photoshop "Radius" slider in pixels. sigma = radius / 3.0. */
  radius: number;
}

// ---------------------------------------------------------------------------
// Kernel construction
// ---------------------------------------------------------------------------

/**
 * Build a normalized 1D Gaussian kernel for the given sigma. Truncated at
 * ±3σ, so the kernel length is `2 * ceil(3σ) + 1`. The returned array sums
 * to 1.0 (within float epsilon).
 *
 * Exported for testing.
 */
export function buildGaussianKernel(sigma: number): Float32Array {
  if (!(sigma > 0)) {
    // Degenerate case: zero blur. Return a 1-tap identity kernel.
    return new Float32Array([1]);
  }
  const half = Math.max(1, Math.ceil(3 * sigma));
  const size = 2 * half + 1;
  const kernel = new Float32Array(size);
  const twoSigmaSq = 2 * sigma * sigma;
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - half;
    const v = Math.exp(-(x * x) / twoSigmaSq);
    kernel[i] = v;
    sum += v;
  }
  // Normalize.
  const inv = 1 / sum;
  for (let i = 0; i < size; i++) {
    kernel[i] = (kernel[i] ?? 0) * inv;
  }
  return kernel;
}

// ---------------------------------------------------------------------------
// Premultiply / unpremultiply
// ---------------------------------------------------------------------------

// Convert RGBA8 → premultiplied Float32 (R*a, G*a, B*a, A) all in 0..255.
// Using Float32 lets us accumulate without overflow / quantization loss.
function premultiply(src: Uint8ClampedArray, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3] ?? 0;
    const af = a / 255;
    out[o] = (src[o] ?? 0) * af;
    out[o + 1] = (src[o + 1] ?? 0) * af;
    out[o + 2] = (src[o + 2] ?? 0) * af;
    out[o + 3] = a;
  }
  return out;
}

// Convert premultiplied Float32 → RGBA8 (Uint8ClampedArray), rounding.
function unpremultiply(
  src: Float32Array,
  w: number,
  h: number,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(w * h * 4);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3] ?? 0;
    if (a <= 0) {
      out[o] = 0;
      out[o + 1] = 0;
      out[o + 2] = 0;
      out[o + 3] = 0;
      continue;
    }
    const inv = 255 / a;
    out[o] = Math.round((src[o] ?? 0) * inv);
    out[o + 1] = Math.round((src[o + 1] ?? 0) * inv);
    out[o + 2] = Math.round((src[o + 2] ?? 0) * inv);
    out[o + 3] = Math.round(a);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Separable convolution passes
// ---------------------------------------------------------------------------

// Horizontal pass. `src` and `dst` are length w*h*4. Clamps x at the edges.
function convolveH(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  kernel: Float32Array,
): void {
  const half = (kernel.length - 1) >> 1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let k = 0; k < kernel.length; k++) {
        let sx = x + k - half;
        if (sx < 0) sx = 0;
        else if (sx >= w) sx = w - 1;
        const o = (row + sx) * 4;
        const wk = kernel[k] ?? 0;
        r += (src[o] ?? 0) * wk;
        g += (src[o + 1] ?? 0) * wk;
        b += (src[o + 2] ?? 0) * wk;
        a += (src[o + 3] ?? 0) * wk;
      }
      const o = (row + x) * 4;
      dst[o] = r;
      dst[o + 1] = g;
      dst[o + 2] = b;
      dst[o + 3] = a;
    }
  }
}

// Vertical pass. Clamps y at the edges.
function convolveV(
  src: Float32Array,
  dst: Float32Array,
  w: number,
  h: number,
  kernel: Float32Array,
): void {
  const half = (kernel.length - 1) >> 1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let k = 0; k < kernel.length; k++) {
        let sy = y + k - half;
        if (sy < 0) sy = 0;
        else if (sy >= h) sy = h - 1;
        const o = (sy * w + x) * 4;
        const wk = kernel[k] ?? 0;
        r += (src[o] ?? 0) * wk;
        g += (src[o + 1] ?? 0) * wk;
        b += (src[o + 2] ?? 0) * wk;
        a += (src[o + 3] ?? 0) * wk;
      }
      const o = (y * w + x) * 4;
      dst[o] = r;
      dst[o + 1] = g;
      dst[o + 2] = b;
      dst[o + 3] = a;
    }
  }
}

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const GaussianBlur: Filter<GaussianBlurParams> = {
  id: "gaussian-blur",
  displayName: "Gaussian Blur",
  defaultParams: { radius: 1.0 },

  apply(
    input: PixelBuffer,
    params: GaussianBlurParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);

    const radius = params.radius;
    // Zero / negative radius → identity. Copy input through.
    if (!(radius > 0) || w === 0 || h === 0) {
      out.data.set(input.data);
      return out;
    }

    const sigma = radius / 3.0;
    const kernel = buildGaussianKernel(sigma);
    if (kernel.length === 1) {
      out.data.set(input.data);
      return out;
    }

    const pre = premultiply(input.data, w, h);
    const tmp = new Float32Array(w * h * 4);
    convolveH(pre, tmp, w, h, kernel);
    convolveV(tmp, pre, w, h, kernel);
    const result = unpremultiply(pre, w, h);
    out.data.set(result);
    return out;
  },
};
